import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { KudosType } from '../../../types'
import { KudosIconIndicator } from '../../../modules/kudos/components/KudosIconIndicator'
import TextArea from 'antd/lib/input/TextArea'
import { createKudosInPublicProfile } from '../../../graphql/kudos/useKudosMutations'
import { getKudosColor } from '../../../shared/kudosUtil'
import { showNotification } from '../Notification/notification'
import CreateKudosModalGuidelines from './CreateKudosModalGuidelines'

interface CreateKudosModalProps {
  visible: boolean
  closeModal: () => void
  employeeId: string
  employeeFullName: string
  onKudosCreated: () => void
}

export const CreateKudosModalInPublicProfile: FC<CreateKudosModalProps> = ({
  visible,
  closeModal,
  employeeId,
  employeeFullName,
  onKudosCreated,
}) => {
  const { t } = useTranslation()

  const [description, setDescription] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [guidelinesAccepted, setGuidelinesAccepted] = useState<boolean>(false)

  const handleCreate = async () => {
    if (!description.trim()) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await createKudosInPublicProfile(employeeId, description.trim())

    if (!!result.data && !result.errors) {
      onKudosCreated()
      resetFormAndCloseModal()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.create.successTitle'),
        description: t('kudos.notifications.create.successDescription'),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.createModal.errors.generic')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetFormAndCloseModal = () => {
    setDescription('')
    setErrorMessage('')
    setGuidelinesAccepted(false)
    setIsLoading(false)
    closeModal()
  }

  return (
    <PerModal
      title={{ heading: t('kudos.createModal.title') }}
      size="large"
      centered
      visible={visible}
      onCancel={resetFormAndCloseModal}
      footer={{
        cancelBtn: {
          text: t('global.button.cancel'),
          onClick: resetFormAndCloseModal,
          icon: 'close_stroke',
          visible: !isLoading,
        },
        confirmBtn: {
          altText: t('kudos.createModal.title'),
          onClick: handleCreate,
          disabled: !description.trim() || isLoading || !guidelinesAccepted,
          isProcessing: { active: isLoading },
          tooltip: !description.trim()
            ? t('kudos.createModal.disabledButtonTooltip')
            : !guidelinesAccepted
            ? t('kudos.createModal.guidelines.tooltip')
            : undefined,
        },
      }}
    >
      <PerFlexRow direction="column">
        {/* Disclaimer */}
        <PerText fontSize="m" mb="m">
          {t('kudos.createModal.disclaimer')}
        </PerText>

        {/* Kudos Info */}
        <PerFlexRow align="middle" mb="m">
          <KudosIconIndicator kudosType={KudosType.Positive} />
          <PerFlexRow align="middle">
            <PerText fontSize="l" ml="xs">
              {t('kudos.createModal.forEmployee')}
            </PerText>
            <PerText fontSize="l" fontWeight="bold" ml="xs" color={getKudosColor(KudosType.Positive)}>
              {employeeFullName}
            </PerText>
          </PerFlexRow>
        </PerFlexRow>

        {errorMessage && (
          <PerText color="error" fontSize="m" mb="m">
            {errorMessage}
          </PerText>
        )}

        {/* Description */}
        <PerFlexRow direction="column">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.description')}
          </PerText>
          <TextArea
            rows={4}
            placeholder={t('kudos.createModal.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
          />
        </PerFlexRow>
        <CreateKudosModalGuidelines guidelinesAccepted={guidelinesAccepted} setGuidelinesAccepted={setGuidelinesAccepted} />
      </PerFlexRow>
    </PerModal>
  )
}

export default CreateKudosModalInPublicProfile
